/**
 * One requirement: its status (which follows a running mission, with an audited override),
 * the live mission, what to fix, description, acceptance criteria, defect details, properties,
 * children with roll-up, links, attachments, missions, where it came from, history and
 * comments. AI can improve it, write criteria, split it or estimate it; each opens a proposal
 * to review. While a mission works the item it never offers to start another.
 *
 * Layout (640), progressive (the session drawer's pattern): the header says kind (a menu),
 * key, project and the title, then the properties as a labelled fact grid whose values become
 * controls on click. The body puts the key content first (what needs attention, the
 * description, acceptance criteria, defect details, the items under it), then the secondary
 * sections folded: links, attachments, missions, where it came from, history and comments. The
 * footer holds the one primary action. Errors show at the top of the body, never in a toast.
 */
import { Bot, ChevronDown, ChevronRight, Eye, ExternalLink, GitCommitHorizontal, Hand, Layers, ListChecks, MoreHorizontal, Pencil, Plus, Rocket, RotateCcw, Ruler, Scissors, Sparkles, Square, Trash2, Wand2, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ITEM_KINDS, ITEM_KIND_LABEL, ITEM_PRIORITIES, ITEM_PRIORITY_LABEL, ITEM_WORKFLOW, SEVERITIES, SEVERITY_LABEL, isJobLive, itemProgress, statusLabel } from '@orgus/protocol';
import type { AiJob, BacklogItem, Id, ItemKind, ItemLink, ItemPatch, ItemPriority, ItemStatus, QualityIssue, Severity } from '@orgus/protocol';
import {
  Button,
  ConfirmDialog,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  IconButton,
  Markdown,
  MarkdownEditor,
  Pressable,
  RelativeTime,
  Textarea,
  Tooltip,
  cn,
  formatInt,
  formatPercent,
  isTypingTarget,
  toast,
} from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.tsx';
import { CommentsThread } from '../attachments/CommentsThread.tsx';
import { uploaderFor, useAttachmentsOn, useAttachmentsState, useResolveAttachment } from '../attachments/data.ts';
import { SessionApprovals } from '../approvals/SessionApprovals.tsx';
import { CriteriaEditor } from '../requirements/CriteriaEditor.tsx';
import { HistorySection, LinksSection, OverrideDialog, SourceSection } from '../requirements/ItemSections.tsx';
import { JobStrip } from '../requirements/AiJobs.tsx';
import { addLink } from '../requirements/data.ts';
import { parentOptions, startAbility } from '../requirements/model.ts';
import { AiCaption, KeyLabel, Rollup, SeverityMark, Tags } from '../requirements/parts.tsx';
import { FACT_GRID } from '../sessions/SessionFacts.tsx';
import { deleteItem, stopMission, updateItem } from './data.ts';
import { plainText } from '../../lib/plainText.ts';
import { useMachines, useOrgus } from '../../lib/store.ts';
import { EMPTY_DASH, EmptyValue, Fact, FactMenu, FactProse, FactText, OptionalFact } from './ItemFacts.tsx';
import { MissionAnswer, useAnswerRoute } from './MissionAnswer.tsx';
import { MissionStages } from './MissionStages.tsx';
import { ReadinessHint } from './Readiness.tsx';
import { missionStages } from './stages.ts';
import { childrenOf, missionsOfItem } from './model.ts';
import type { MissionRow } from './model.ts';
import { ItemState, ItemStatusIcon, KindIcon, MissionStatePill, PriorityMark, ProgressBar } from './parts.tsx';

export type AiItemTask = 'improve' | 'criteria' | 'split' | 'estimate';

/** Key content: always open, a title with a quiet count and an action on its right. */
function Section({ title, count, action, children, id }: { title: string; count?: ReactNode; action?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="flex flex-col gap-2 px-6 py-4" aria-labelledby={id}>
      <header className="flex h-control-sm items-center justify-between gap-2">
        <h3 id={id} className="flex min-w-0 items-baseline gap-2 text-ui font-medium text-fg">
          <span className="truncate">{title}</span>
          {count != null && <span className="shrink-0 text-caption font-normal text-fg-subtle num">{count}</span>}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * Secondary content, folded: one quiet row with a chevron, the title and a count. The content
 * stays mounted while folded (hidden), so a paste still reaches the attachments.
 */
function Fold({ title, count, open, onToggle, action, children, id }: { title: string; count?: number; open: boolean; onToggle: () => void; action?: ReactNode; children: ReactNode; id: string }) {
  return (
    <section className="flex flex-col px-4" aria-labelledby={`${id}-title`}>
      <div className="group/fold flex h-row items-center gap-2">
        <Pressable
          id={`${id}-title`}
          aria-expanded={open}
          aria-controls={id}
          onClick={onToggle}
          className="flex h-control-sm min-w-0 flex-1 items-center gap-2 rounded-control px-2 text-ui text-fg-muted hover:bg-hover hover:text-fg"
        >
          <ChevronRight className={cn('size-4 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />
          <span className="truncate font-medium">{title}</span>
          {count != null && count > 0 && <span className="shrink-0 text-caption text-fg-subtle num">{formatInt(count)}</span>}
        </Pressable>
        {action && <span className={cn('shrink-0', !open && 'opacity-0 group-hover/fold:opacity-100 focus-within:opacity-100')}>{action}</span>}
      </div>
      <div id={id} hidden={!open} className="px-2 pb-4 pt-1">
        {children}
      </div>
    </section>
  );
}

const csv = (s: string) => [...new Set(s.split(',').map((x) => x.trim()).filter(Boolean))];

/** A roll-up note the hub wrote ("Follows its items: 0 of 9 done.") goes stale; the drawer says it from today's numbers. */
const isRollupNote = (text: string) => /^Follows its items\b/.test(text);

/** The facts: the session drawer's grid, two even columns on a phone and four from 640 px, so every row lines up. */
const META_ROW = FACT_GRID;

/** What the mission asks, alone: the Needs you pill already names the state. */
const askText = (title: string) => plainText(title).replace(/^Asks:\s*/, '');

/**
 * The title, edited where it reads: a one-line text area that wraps (the whole title always in
 * view), at the title's size and weight. Enter or leaving it saves, Escape puts it back.
 */
function TitleEdit({ value, onSave, onCancel }: { value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(value);
  const done = useRef(false);
  const commit = () => {
    if (done.current) return;
    done.current = true;
    const v = draft.replace(/\s+/g, ' ').trim();
    if (v && v !== value) onSave(v);
    else onCancel();
  };
  return (
    <Textarea
      aria-label="Title"
      rows={1}
      autoFocus
      value={draft}
      maxLength={200}
      onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          done.current = true;
          onCancel();
        }
      }}
      wrapperClassName="-mx-2 min-w-0 flex-1"
      className="min-h-0 resize-none px-2 py-0 text-lead field-sizing-content pointer-coarse:text-(length:--touch-input-font)"
    />
  );
}

/** Children shown before "Show all": the list stays a glance, the rest is one click away. */
const CHILDREN_AT_REST = 6;

type FoldId = 'links' | 'files' | 'missions' | 'source' | 'activity';
type Editing = 'owner' | 'estimate' | 'labels' | 'components' | null;

export function ItemDrawer({
  item,
  items,
  links,
  rows,
  issues,
  jobs,
  projectName,
  onClose,
  onOpenItem,
  onOpenMission,
  onStart,
  onRetry,
  onStartSequence,
  onAddChild,
  onAiTask,
  onReviewProposal,
  error: pageError,
  onDismissError,
}: {
  item: BacklogItem | undefined;
  items: readonly BacklogItem[];
  links: readonly ItemLink[];
  rows: readonly MissionRow[];
  issues: ReadonlyMap<Id, QualityIssue[]>;
  jobs: readonly AiJob[];
  projectName: (id: Id) => string;
  onClose: () => void;
  onOpenItem: (id: Id) => void;
  onOpenMission: (id: Id) => void;
  onStart: (item: BacklogItem) => void;
  /** Retry a mission of this item that failed or stopped. */
  onRetry: (row: MissionRow) => void;
  onStartSequence: (item: BacklogItem) => void;
  onAddChild: (item: BacklogItem) => void;
  onAiTask: (task: AiItemTask, item: BacklogItem) => void;
  onReviewProposal: (id: Id) => void;
  /** An action the page ran for this item failed (an AI task, a move). */
  error?: UiError | null;
  onDismissError?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [body, setBody] = useState('');
  const [field, setField] = useState<Editing>(null);
  const pendingField = useRef<Editing>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<UiError | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const navigate = useNavigate();
  const machines = useMachines();
  const sessions = useOrgus((st) => st.sessions);
  const [openFile, setOpenFile] = useState<string | null>(null);
  const [override, setOverride] = useState<ItemStatus | null>(null);
  /** Done or dropped while a mission works the item: stop the mission first, after asking. */
  const [finishing, setFinishing] = useState<ItemStatus | null>(null);
  const [unfolded, setUnfolded] = useState(false);
  const [allChildren, setAllChildren] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [folds, setFolds] = useState<ReadonlySet<FoldId>>(new Set());
  const [linkPreset, setLinkPreset] = useState<string | null>(null);
  const filesOn = !!useAttachmentsState();
  const target = useMemo(() => (item ? { kind: 'item' as const, id: item.id } : null), [item?.id]);
  const attached = useAttachmentsOn(target);
  const resolve = useResolveAttachment();
  const upload = useMemo(() => (target ? uploaderFor(target) : undefined), [target]);

  useEffect(() => {
    setEditing(false);
    setEditingTitle(false);
    setUnfolded(false);
    setAllChildren(false);
    setAnswering(false);
    setFolds(new Set());
    setField(null);
    setSaveError(null);
    setBody(item?.body ?? '');
    // Reset when another item opens, not on every live update of this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const children = useMemo(() => (item ? childrenOf([...items], item.id) : []), [items, item]);
  const missions = useMemo(() => (item ? missionsOfItem(rows, item.id) : []), [rows, item]);
  const ability = item ? startAbility(item, rows, items) : null;
  const live = ability?.kind === 'running' ? ability.row : null;
  // Needs you is never action-less: what the mission waits on is answered here, in place.
  const answerRoute = useAnswerRoute(live);
  const canAnswer = !!live && live.view.state === 'waiting' && !!answerRoute;
  const openAnswer = () => setAnswering(true);
  useEffect(() => {
    // The answer opens where the question is: bring it into view once it has rendered.
    if (answering) document.getElementById('item-answer')?.scrollIntoView({ block: 'nearest' });
  }, [answering]);
  const parents = useMemo(() => (item ? parentOptions(items, item) : []), [items, item]);
  const parent = item?.parentId ? items.find((i) => i.id === item.parentId) : undefined;
  const linkCount = item ? links.filter((l) => l.from === item.id || l.to === item.id).length : 0;
  const hasCriteriaSection = !!item && item.kind !== 'epic' && item.kind !== 'feature';
  // The description and criteria sections offer their own AI fix, and the Parent field its own picker (QA-20), so those findings are not listed twice.
  const myIssues = (item ? (issues.get(item.id) ?? []) : []).filter((q) => q.code !== 'no_description' && q.code !== 'no_parent' && !(q.code === 'no_criteria' && hasCriteriaSection));
  const myJobs = jobs.filter((j) => item && j.itemId === item.id && (isJobLive(j.state) || (j.state === 'done' && j.proposalId) || j.state === 'failed'));
  const liveJobs = myJobs.filter((j) => isJobLive(j.state));
  const pastJobs = myJobs.filter((j) => !isJobLive(j.state));
  const liveSession = live ? ([...live.view.sessions].reverse().find((x) => x.session)?.session ?? null) : null;
  // A finished mission whose result waits for a person: the footer offers Review, not another start.
  const toReview = item?.status === 'review' ? missions.find((r) => r.view.state === 'done' && !r.mission.closed && !r.mission.landed) : undefined;
  // Where a new mission would run: the machine this project's sessions used last, as the start dialog picks it.
  const runsOn = useMemo(() => {
    if (!item) return null;
    const online = new Set(machines.filter((m) => m.online).map((m) => m.id));
    const last = Object.values(sessions)
      .filter((x) => x.projectId === item.projectId && online.has(x.machineId))
      .sort((a, b) => b.lastActivity - a.lastActivity)[0];
    return last ? (machines.find((m) => m.id === last.machineId)?.name ?? null) : null;
  }, [item?.projectId, machines, sessions]); // eslint-disable-line react-hooks/exhaustive-deps
  const error = saveError ?? pageError ?? null;

  const toggle = (f: FoldId, to?: boolean) =>
    setFolds((s) => {
      const n = new Set(s);
      if (to ?? !n.has(f)) n.add(f);
      else n.delete(f);
      return n;
    });
  useEffect(() => {
    // A fix that adds a link opens the links section it lands in.
    if (linkPreset) toggle('links', true);
  }, [linkPreset]);

  /** Saves a change. An inline value needs no toast (the value itself shows it); a move says where it went. */
  const save = async (patch: ItemPatch, what?: string) => {
    if (!item) return false;
    setSaving(true);
    setSaveError(null);
    try {
      await updateItem(item.id, patch);
      if (what) toast.success(what, `${item.key} ${item.title}`);
      return true;
    } catch (e) {
      setSaveError(toUiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = (s: ItemStatus) => {
    if (!item || s === item.status) return;
    if (live && (s === 'done' || s === 'dropped')) setFinishing(s);
    else if (live) setOverride(s);
    else void save({ status: s }, `Moved to ${statusLabel(item.kind, s)}`);
  };

  // A long description folds after about 6 lines, so criteria and the items under it stay in reach.
  const longBody = !!item && (item.body.split('\n').length > 6 || item.body.length > 420);

  const fix = (q: QualityIssue) => {
    if (!item || !q.fix) return;
    switch (q.fix.kind) {
      case 'ai':
        onAiTask(q.fix.task, item);
        break;
      case 'pick-parent':
        document.getElementById('item-parent')?.click();
        break;
      case 'pick-affects':
        setLinkPreset('affects:out');
        break;
      case 'mark-duplicate':
        setSaveError(null);
        void addLink({ type: 'duplicates', from: item.id, to: q.fix.otherId }).then(
          () => toast.success('Marked as duplicate', item.key),
          (e) => setSaveError(toUiError(e)),
        );
        break;
      case 'edit':
        setEditing(true);
        break;
    }
  };

  const open = !!item;
  const canSplit = item && (item.kind === 'story' || item.kind === 'feature' || item.kind === 'epic');
  const canHoldChildren = item && (item.kind === 'epic' || item.kind === 'feature' || item.kind === 'story' || item.kind === 'defect');
  const flow = item ? ITEM_WORKFLOW[item.kind] : [];

  const runPrimary = () => {
    if (!item || !ability) return;
    if (toReview) onOpenMission(toReview.mission.id);
    else if (canAnswer) openAnswer();
    else if (ability.kind === 'running') onOpenMission(ability.row.mission.id);
    else if (ability.kind === 'retry') onRetry(ability.row);
    else if (ability.kind === 'mission') onStart(item);
    else if (ability.kind === 'sequence') onStartSequence(item);
  };
  const openStages = (r: MissionRow) => missionStages(r.mission, r.view);
  /** "1 working, 1 needs you": what its sessions do now, or the whole progress line when none is busy. */
  const sessionsNow = (r: MissionRow) => {
    const c = r.view.counts;
    const busy = c.working + c.starting;
    const bits = [busy && `${formatInt(busy)} working`, c.waiting_for_human && `${formatInt(c.waiting_for_human)} ${c.waiting_for_human === 1 ? 'needs' : 'need'} you`].filter((b): b is string => !!b);
    return bits.length ? bits.join(', ') : r.view.progressLabel;
  };
  /** The one primary action for the item's state, with the line beside it that says what it does. */
  const [primary, footerNote] = (() => {
    if (!item || !ability) return [null, null];
    const button = (icon: ReactNode, label: string, hint: string) => (
      <Tooltip content={hint} shortcut={['m']}>
        <Button variant="primary" icon={icon} onClick={runPrimary}>
          {label}
        </Button>
      </Tooltip>
    );
    if (toReview) return [button(<Eye />, 'Review result', 'Opens the mission at its review'), <MissionStages key="s" stages={openStages(toReview)} variant="compact" />];
    switch (ability.kind) {
      case 'running':
        return [
          canAnswer ? button(<Hand />, 'Answer', 'Answers what the mission waits on, here') : button(<Rocket />, 'Open mission', 'Opens the mission at work on this item'),
          // The stepper above shows the stages; the footer says what its sessions do now, in one short fact.
          <span key="s" className="truncate text-caption text-fg-subtle">{sessionsNow(ability.row)}</span>,
        ];
      case 'retry':
        return [button(<RotateCcw />, 'Retry mission', 'Starts it again like its last start'), <span key="s" className="truncate text-caption text-fg-subtle">Its last mission {ability.row.view.state === 'failed' ? 'failed' : 'was stopped'}</span>];
      case 'mission':
      case 'sequence': {
        const note = ability.kind === 'sequence' ? 'One story at a time' : runsOn ? `Runs on ${runsOn}` : 'On a machine you pick';
        return [
          button(ability.kind === 'sequence' ? <Layers /> : <Rocket />, ability.kind === 'sequence' ? 'Run as a sequence' : missions.length ? 'Start another mission' : 'Start mission', ability.kind === 'sequence' ? 'Runs its open stories one mission after another' : 'Puts Claude Code on this item'),
          <span key="s" className="flex min-w-0 items-center gap-1 text-caption text-fg-subtle">
            <span className="truncate">{note}</span>
            <Pressable className="w-auto shrink-0 rounded-control px-1 text-accent-text hover:underline" onClick={runPrimary}>
              Change
            </Pressable>
          </span>,
        ];
      }
      default:
        return [null, <span key="s" className="truncate text-ui text-fg-subtle">{ability.reason}</span>];
    }
  })();

  // The status cell's one dim line: why the status is what it is, from today's numbers.
  const statusSub = (() => {
    if (!item) return null;
    const note = item.statusNote;
    if (item.statusPinned) {
      return (
        <span className="flex min-w-0 items-center gap-1">
          <Hand className="size-3 shrink-0" aria-hidden />
          <Tooltip content={note?.kind === 'override' ? `${note.by}: ${note.text}` : 'Set by hand'}>
            <span tabIndex={0} className="truncate">
              Set by hand
            </span>
          </Tooltip>
          <Pressable className="w-auto shrink-0 rounded-control text-accent-text hover:underline" onClick={() => void save({ pinned: false }, 'Status follows the mission again')}>
            Follow again
          </Pressable>
        </span>
      );
    }
    // QA-85: a main commit named this item; Autopilot skips it until it is done or handed back.
    if (item.cited && item.status !== 'done' && item.status !== 'dropped') {
      const c = item.cited;
      return (
        <span className="flex min-w-0 items-center gap-1">
          <GitCommitHorizontal className="size-3 shrink-0" aria-hidden />
          <Tooltip content={`${c.subject}. Autopilot skips it until it is done.`}>
            <span tabIndex={0} className="truncate">
              Landed in <span className="font-mono">{c.sha.slice(0, 7)}</span>
            </span>
          </Tooltip>
          <Pressable className="w-auto shrink-0 rounded-control text-accent-text hover:underline" onClick={() => void save({ cited: null }, 'Autopilot may take it again')}>
            Hand back
          </Pressable>
        </span>
      );
    }
    if (!note) return null;
    if (isRollupNote(note.text)) {
      const p = itemProgress(item, items);
      return (
        <Tooltip content={`${formatPercent(p.fraction)} of the work, ${formatInt(p.done)} of ${formatInt(p.total)} done`}>
          <span tabIndex={0} className="truncate">
            Follows its items
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip content={note.kind === 'override' ? `${note.by} set it by hand: ${note.text}` : note.text}>
        <span tabIndex={0} className="flex min-w-0 items-center gap-1">
          {note.kind === 'mission' ? <Bot className="size-3 shrink-0" aria-hidden /> : <Hand className="size-3 shrink-0" aria-hidden />}
          <span className="truncate">{note.kind === 'mission' ? 'Follows its mission' : 'Set by hand'}</span>
        </span>
      </Tooltip>
    );
  })();

  const shownChildren = allChildren ? children : children.slice(0, CHILDREN_AT_REST);
  const defect = item?.kind === 'defect' ? item.defect : undefined;
  // A defect's points show once set (or being set from More actions); unset, they stay out of the row.
  const defectPoints = !!defect && (item?.estimate != null || field === 'estimate');
  /**
   * On a phone the parent takes a whole row, so the grid's child-order dividers would land on the
   * wrong cells: each cell after it says which of the two columns it sits in from what shows there.
   */
  const phoneCol = (cell: 'points' | 'labels' | 'components') => {
    if (!item) return undefined;
    const shown = [defectPoints && 'points', (item.labels.length > 0 || field === 'labels') && 'labels', (item.components.length > 0 || field === 'components') && 'components'].filter(Boolean);
    const at = shown.indexOf(cell);
    if (at < 0) return undefined;
    // On a phone Parent takes the first half of the row and these follow it, two to a row: the
    // right-hand cell gets the inset divider, the left-hand one none (hidden empty cells do not count).
    return (at + 1) % 2 ? 'max-sm:pl-4! max-sm:before:block!' : 'max-sm:pl-0! max-sm:before:hidden!';
  };
  /** Parent spans the phone row only when nothing shares it; from 640 px it takes two of the four columns beside labels and components. */
  const parentCol = (() => {
    if (!item) return undefined;
    const followers = [defectPoints, item.labels.length > 0 || field === 'labels', item.components.length > 0 || field === 'components'].filter(Boolean).length;
    return cn(followers === 0 && 'max-sm:col-span-2', !defectPoints && 'sm:col-span-2');
  })();
  const points = item && (
    <span className="group/points flex min-w-0 items-center gap-1">
      <FactText
        label="Points"
        value={item.estimate != null ? String(item.estimate) : ''}
        display={<span className="truncate num">{item.estimate != null ? formatInt(item.estimate) : ''}</span>}
        empty={EMPTY_DASH}
        addLabel="Add"
        inputMode="numeric"
        maxLength={6}
        clean={(v) => v.replace(/[^\d.]/g, '')}
        editing={field === 'estimate'}
        onEditingChange={(v) => setField(v ? 'estimate' : null)}
        onSave={(v) => {
          const n = v === '' ? null : Number(v);
          if (n === null || Number.isFinite(n)) void save({ estimate: n });
        }}
      />
      {/* AI help sits on the value it improves. */}
      {field !== 'estimate' && (
        <span className="opacity-0 transition-opacity duration-(--duration-fast) group-hover/points:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
          <IconButton label="Estimate with AI" icon={<Sparkles />} size="xs" onClick={() => onAiTask('estimate', item)} />
        </span>
      )}
    </span>
  );

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(v) => !v && onClose()}
        width={640}
        layout="rows"
        onKeyDown={(e) => {
          if (!item || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
          if (e.key === 'm' && ability && ability.kind !== 'none') {
            e.preventDefault();
            runPrimary();
          } else if (e.key === 'e') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        eyebrow={
          item && (
            <>
              {/* The kind is a tiny label like every fact, and the menu that changes it. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Pressable className="group/kind -ml-1 flex h-6 w-auto shrink-0 items-center gap-1 rounded-control px-1 text-caption text-fg-subtle hover:bg-hover hover:text-fg-muted aria-expanded:bg-hover" aria-label={`Kind: ${ITEM_KIND_LABEL[item.kind]}. Change it`}>
                    <KindIcon kind={item.kind} tooltip={false} />
                    <span>{ITEM_KIND_LABEL[item.kind]}</span>
                    <ChevronDown className="size-3 opacity-0 transition-opacity duration-(--duration-fast) group-hover/kind:opacity-100 group-focus-visible/kind:opacity-100" aria-hidden />
                  </Pressable>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" aria-label="Kind">
                  <DropdownMenuRadioGroup value={item.kind} onValueChange={(v) => v !== item.kind && void save({ kind: v as ItemKind }, `Now ${ITEM_KIND_LABEL[v as ItemKind].toLowerCase()}`)}>
                    {ITEM_KINDS.map((k) => (
                      <DropdownMenuRadioItem key={k} value={k} className="gap-2">
                        <KindIcon kind={k} tooltip={false} />
                        {ITEM_KIND_LABEL[k]}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <KeyLabel value={item.key} />
              <span className="min-w-0 truncate text-fg-subtle">{projectName(item.projectId)}</span>
              {item.aiDrafted && <AiCaption className="shrink-0 max-sm:hidden" />}
              <RelativeTime ts={item.updatedAt} prefix="Updated" className="ml-auto hidden shrink-0 pr-2 text-caption text-fg-subtle sm:inline" />
            </>
          )
        }
        title={
          item ? (
            editingTitle ? (
              <TitleEdit
                key={item.id}
                value={item.title}
                onCancel={() => setEditingTitle(false)}
                onSave={(v) => {
                  setEditingTitle(false);
                  void save({ title: v, ...(item.aiDrafted ? { aiDrafted: false } : {}) });
                }}
              />
            ) : (
              <Pressable className="-mx-1 w-auto rounded-control px-1 text-left hover:bg-hover" onClick={() => setEditingTitle(true)} aria-label={`${item.title}. Edit the title`}>
                {item.title}
              </Pressable>
            )
          ) : (
            'Item'
          )
        }
        actions={
          item && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton label="Ask AI" icon={<Sparkles />} size="sm" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem icon={<Wand2 />} onSelect={() => onAiTask('improve', item)}>
                    Improve the wording
                  </DropdownMenuItem>
                  <DropdownMenuItem icon={<ListChecks />} onSelect={() => onAiTask('criteria', item)}>
                    Write acceptance criteria
                  </DropdownMenuItem>
                  {canSplit && (
                    <DropdownMenuItem icon={<Scissors />} onSelect={() => onAiTask('split', item)}>
                      {item.kind === 'story' ? 'Split into smaller stories' : 'Break down into stories'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem icon={<Ruler />} onSelect={() => onAiTask('estimate', item)}>
                    Estimate
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <IconButton label="Edit description" icon={<Pencil />} size="sm" shortcut={['e']} onClick={() => setEditing(true)} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton label="More actions" icon={<MoreHorizontal />} size="sm" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  onCloseAutoFocus={(e) => {
                    // Adding labels opens their text box: focus goes there, not back to the menu button.
                    if (!pendingField.current) return;
                    e.preventDefault();
                    setField(pendingField.current);
                    pendingField.current = null;
                  }}
                >
                  {canHoldChildren && (
                    <DropdownMenuItem icon={<Plus />} onSelect={() => onAddChild(item)}>
                      Add an item under it…
                    </DropdownMenuItem>
                  )}
                  {item.kind === 'defect' && item.defect && item.estimate == null && (
                    <DropdownMenuItem icon={<Plus />} onSelect={() => (pendingField.current = 'estimate')}>
                      Add points
                    </DropdownMenuItem>
                  )}
                  {item.labels.length === 0 && (
                    <DropdownMenuItem icon={<Plus />} onSelect={() => (pendingField.current = 'labels')}>
                      Add labels
                    </DropdownMenuItem>
                  )}
                  {item.components.length === 0 && (
                    <DropdownMenuItem icon={<Plus />} onSelect={() => (pendingField.current = 'components')}>
                      Add components
                    </DropdownMenuItem>
                  )}
                  {ability?.kind === 'retry' && (
                    <DropdownMenuItem icon={<Rocket />} onSelect={() => onStart(item)}>
                      Start a new mission…
                    </DropdownMenuItem>
                  )}
                  {live && (
                    <DropdownMenuItem icon={<Square />} onSelect={() => setConfirmStop(true)}>
                      Stop mission…
                    </DropdownMenuItem>
                  )}
                  {item.status !== 'dropped' && (
                    <DropdownMenuItem icon={<X />} onSelect={() => changeStatus('dropped')}>
                      {item.kind === 'defect' ? 'Will not fix' : 'Drop it'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem icon={<Trash2 />} danger onSelect={() => setConfirmDelete(true)}>
                    Delete…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )
        }
        footer={
          item && (
            <>
              <span className="mr-auto flex min-w-0 items-center gap-3">{footerNote}</span>
              {primary}
            </>
          )
        }
      >
        {item && (
          <div className="flex flex-col pb-4">
            {error && (
              <div className="px-6 pb-4">
                <ErrorState
                  variant="inline"
                  error={error}
                  action={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSaveError(null);
                        onDismissError?.();
                      }}
                    >
                      Dismiss
                    </Button>
                  }
                />
              </div>
            )}

            {/* The facts scroll with the body, so on a phone the header stays the title alone. Both rows share the session drawer's grid, so every cell lines up. */}
            <div className="group/facts flex flex-col gap-4 px-6 pb-4">
              <dl className={META_ROW}>
                <Fact label="Status" sub={statusSub}>
                  <FactMenu
                    label="Status"
                    value={item.status}
                    options={flow.map((s) => ({ value: s, label: statusLabel(item.kind, s), icon: <ItemStatusIcon status={s} /> }))}
                    fit
                    covers
                    display={<ItemState status={item.status} kind={item.kind} />}
                    onChange={(v) => changeStatus(v as ItemStatus)}
                  />
                </Fact>
                <Fact label="Priority">
                  <FactMenu
                    label="Priority"
                    value={String(item.priority)}
                    options={ITEM_PRIORITIES.map((p) => ({ value: String(p), label: ITEM_PRIORITY_LABEL[p], icon: <PriorityMark priority={p} /> }))}
                    fit
                    display={<PriorityMark priority={item.priority} withLabel />}
                    onChange={(v) => void save({ priority: Number(v) as ItemPriority })}
                  />
                </Fact>
                {/* A defect's severity is its most important fact: it takes the third cell, and points move to the second row. */}
                {defect ? (
                  <Fact label="Severity">
                    <FactMenu
                      label="Severity"
                      value={defect.severity}
                      options={SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABEL[s], icon: <SeverityMark severity={s} label={false} /> }))}
                      fit
                      display={<SeverityMark severity={defect.severity} />}
                      onChange={(v) => void save({ defect: { severity: v as Severity } }, `Severity set to ${SEVERITY_LABEL[v as Severity]}`)}
                    />
                  </Fact>
                ) : (
                  <Fact label="Points">{points}</Fact>
                )}
                <Fact label="Owner">
                  <FactText label="Owner" value={item.owner ?? ''} empty={EMPTY_DASH} addLabel="Add" editing={field === 'owner'} onEditingChange={(v) => setField(v ? 'owner' : null)} onSave={(v) => void save({ owner: v || null })} />
                </Fact>
              </dl>
              {/* The parent (long free text: it truncates, its whole name in a tooltip), then labels and components once set. */}
              <dl className={META_ROW}>
                <Fact label="Parent" className={parentCol}>
                  <FactMenu
                    id="item-parent"
                    label="Parent"
                    wide
                    tooltip={parent ? `${parent.key} ${parent.title}` : undefined}
                    value={item.parentId ?? ''}
                    options={[{ value: '', label: 'None' }, ...parents.map((p) => ({ value: p.id, label: `${p.key} ${p.title}`, icon: <KindIcon kind={p.kind} tooltip={false} /> }))]}
                    display={parent ? <span className="min-w-0 truncate">{parent.title}</span> : <EmptyValue text={EMPTY_DASH} add="Add" />}
                    onChange={(v) => void save({ parentId: v || null })}
                  />
                </Fact>
                {defectPoints && <Fact label="Points" className={phoneCol('points')}>{points}</Fact>}
                <OptionalFact label="Labels" set={item.labels.length > 0} editing={field === 'labels'} onAdd={() => setField('labels')} addLabel="Add labels" className={phoneCol('labels')}>
                  <FactText
                    label="Labels, comma separated"
                    value={item.labels.join(', ')}
                    display={<Tags values={item.labels} />}
                    empty={EMPTY_DASH}
                    editing={field === 'labels'}
                    onEditingChange={(v) => setField(v ? 'labels' : null)}
                    onSave={(v) => void save({ labels: csv(v) })}
                  />
                </OptionalFact>
                <OptionalFact label="Components" set={item.components.length > 0} editing={field === 'components'} onAdd={() => setField('components')} addLabel="Add components" className={phoneCol('components')}>
                  <FactText
                    label="Components, comma separated"
                    value={item.components.join(', ')}
                    display={<Tags values={item.components} />}
                    empty={EMPTY_DASH}
                    editing={field === 'components'}
                    onEditingChange={(v) => setField(v ? 'components' : null)}
                    onSave={(v) => void save({ components: csv(v) })}
                  />
                </OptionalFact>
              </dl>
            </div>

            {live && (
              // The mission at work: where it is in its stages, and what it waits on. A line, not a card.
              <div className="flex flex-col gap-3 px-6 pb-6" aria-label="Mission working on this item">
                <div className="flex h-control-sm min-w-0 items-center gap-3">
                  <MissionStatePill state={live.view.state} blockers={live.view.blockers} />
                  <Pressable className="w-auto min-w-0 truncate rounded-control text-ui text-fg hover:underline" onClick={() => onOpenMission(live.mission.id)}>
                    {live.mission.title}
                  </Pressable>
                  {liveSession && (
                    <span className="ml-auto shrink-0">
                      <IconButton label="Open its session" icon={<ExternalLink />} size="xs" onClick={() => navigate(`/sessions?session=${encodeURIComponent(liveSession.id)}`)} />
                    </span>
                  )}
                </div>
                <MissionStages stages={missionStages(live.mission, live.view)} />
                {live.view.blockers[0] && (
                  // The agent's question in a quiet card with a tiny label, so it never reads as the description's first line. The pill above names the state; the footer's Answer opens the reply here.
                  <div className="flex min-w-0 flex-col gap-1 rounded-control border border-border-subtle bg-bg px-3 py-2" aria-label="What the mission asks">
                    <span className="text-caption text-fg-subtle">{/^Asks:/.test(plainText(live.view.blockers[0].title)) ? 'Asks' : 'Waits on'}</span>
                    <p className="line-clamp-3 min-w-0 text-ui text-fg">{askText(live.view.blockers[0].title)}</p>
                  </div>
                )}
                {canAnswer && answering && (
                  <div id="item-answer" className="flex flex-col gap-2">
                    <MissionAnswer row={live} route={answerRoute} />
                    <div>
                      <Button variant="ghost" size="sm" className="-ml-2" aria-expanded aria-controls="item-answer" onClick={() => setAnswering(false)}>
                        Hide answer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {myIssues.length > 0 && <ReadinessHint issues={myIssues} onFix={fix} className="px-6 pb-3" />}
            {/* A job running now is a slim line here; finished ones live in Activity. */}
            {liveJobs.length > 0 && (
              <div className="flex flex-col px-6 pb-3" aria-label="AI jobs running on this item">
                {liveJobs.map((j) => (
                  <JobStrip key={j.id} job={j} onReview={onReviewProposal} />
                ))}
              </div>
            )}

            {/* Description */}
            <div className="flex flex-col gap-3 px-6 pb-4">
              {editing ? (
                <>
                  {/* The title edits in place in the header; here only the description, where it reads. */}
                  <MarkdownEditor
                    label="Description"
                    rows={8}
                    autoFocus
                    value={body}
                    onChange={setBody}
                    maxLength={20_000}
                    upload={filesOn ? upload : undefined}
                    resolveAttachment={resolve}
                    hint={item.kind === 'story' ? 'As a <role>, I want <goal>, so that <benefit>. Paste a screenshot to put it in the text.' : 'Markdown. A ```mermaid fence draws a diagram. Paste a screenshot to put it in the text.'}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditing(false);
                        setBody(item.body);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      loading={saving}
                      onClick={async () => {
                        if (await save({ body, ...(item.aiDrafted ? { aiDrafted: false } : {}) })) setEditing(false);
                      }}
                    >
                      Save changes
                    </Button>
                  </div>
                </>
              ) : item.body.trim() ? (
                <div className="flex flex-col gap-2">
                  <div className={longBody && !unfolded ? 'max-h-32 overflow-hidden mask-b-from-60%' : undefined}>
                    {/* The soft body colour, at the UI size: the title and values stay brightest. */}
                    <Markdown source={item.body} size="sm" resolveAttachment={resolve} onOpenAttachment={setOpenFile} className="max-w-(--layout-width-prose) text-fg-muted" />
                  </div>
                  {longBody && (
                    <div>
                      <Button variant="ghost" size="sm" className="-ml-2" icon={unfolded ? <ChevronDown className="rotate-180" /> : <ChevronDown />} aria-expanded={unfolded} onClick={() => setUnfolded((v) => !v)}>
                        {unfolded ? 'Show less' : 'Show more'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mr-auto text-ui text-fg-subtle">No description yet</span>
                  <Button variant="ghost" size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
                    Write it
                  </Button>
                  <Button variant="ghost" size="sm" icon={<Wand2 />} onClick={() => onAiTask('improve', item)}>
                    Draft with AI
                  </Button>
                </div>
              )}
            </div>

            {hasCriteriaSection ? (
              <Section title="Acceptance criteria" count={item.acceptance.length ? `${formatInt(item.acceptance.filter((c) => c.done).length)} of ${formatInt(item.acceptance.length)} met` : undefined} id="item-ac">
                <CriteriaEditor item={item} onGenerate={() => onAiTask('criteria', item)} />
              </Section>
            ) : (
              item.acceptance.length > 0 && (
                <Section title="Acceptance criteria" count={`${formatInt(item.acceptance.filter((c) => c.done).length)} of ${formatInt(item.acceptance.length)} met`} id="item-ac">
                  <CriteriaEditor item={item} />
                </Section>
              )
            )}

            {item.kind === 'defect' && item.defect && (
              <Section title="Defect" id="item-defect">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <span className="text-caption text-fg-subtle">Environment</span>
                    <FactProse label="Environment" rows={2} value={item.defect.environment} empty="Not said" onSave={(v) => void save({ defect: { environment: v } })} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-caption text-fg-subtle">Steps to reproduce</span>
                    <FactProse label="Steps to reproduce" value={item.defect.steps} empty="Not written" onSave={(v) => void save({ defect: { steps: v } })} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="text-caption text-fg-subtle">Expected</span>
                      <FactProse label="Expected" rows={2} value={item.defect.expected} empty="Not written" onSave={(v) => void save({ defect: { expected: v } })} />
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <span className="text-caption text-fg-subtle">Actual</span>
                      <FactProse label="Actual" rows={2} value={item.defect.actual} empty="Not written" onSave={(v) => void save({ defect: { actual: v } })} />
                    </div>
                  </div>
                </div>
              </Section>
            )}

            {(children.length > 0 || item.kind === 'epic' || item.kind === 'feature') && (
              <Section
                title="Under it"
                count={children.length ? formatInt(children.length) : undefined}
                action={
                  <span className="flex items-center gap-3">
                    {/* The figure counts the rows listed here, so it agrees with the count beside the title. */}
                    {children.length > 0 && <Rollup item={item} items={items} wide direct />}
                    <IconButton label="Add an item under it" icon={<Plus />} size="sm" onClick={() => onAddChild(item)} />
                  </span>
                }
              >
                {children.length ? (
                  <ul className="-mx-2 flex flex-col" data-list aria-label="Items under it">
                    {shownChildren.map((c) => (
                      <li key={c.id}>
                        <Pressable data-row className="flex h-row items-center gap-3 rounded-control px-2 hover:bg-hover" onClick={() => onOpenItem(c.id)}>
                          <KindIcon kind={c.kind} tooltip={false} />
                          <KeyLabel value={c.key} className="w-20 max-sm:hidden" />
                          <span className="min-w-0 flex-1 truncate text-ui text-fg">{c.title}</span>
                          <ItemState status={c.status} kind={c.kind} className="w-28" />
                        </Pressable>
                      </li>
                    ))}
                    {children.length > CHILDREN_AT_REST && (
                      <li>
                        <Button variant="ghost" size="sm" className="ml-1 mt-1" aria-expanded={allChildren} icon={<ChevronDown className={allChildren ? 'rotate-180' : undefined} />} onClick={() => setAllChildren((v) => !v)}>
                          {allChildren ? 'Show fewer' : `Show all ${formatInt(children.length)}`}
                        </Button>
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="text-ui text-fg-subtle">Nothing under it yet</p>
                )}
              </Section>
            )}

            {/* Secondary: folded, one quiet row each, opened on demand. */}
            <div className="mt-2 flex flex-col border-t border-border-subtle pt-2">
              {/* Activity: what AI did on it, the conversation, and every change. */}
              <Fold id="item-activity" title="Activity" count={pastJobs.length} open={folds.has('activity')} onToggle={() => toggle('activity')}>
                <div className="flex flex-col gap-4">
                  {pastJobs.length > 0 && (
                    <div className="flex flex-col" aria-label="AI jobs on this item">
                      {pastJobs.map((j) => (
                        <JobStrip key={j.id} job={j} onReview={onReviewProposal} />
                      ))}
                    </div>
                  )}
                  {target && <CommentsThread target={target} />}
                  {folds.has('activity') && <HistorySection item={item} open />}
                </div>
              </Fold>

              <Fold id="item-links" title="Links" count={linkCount} open={folds.has('links')} onToggle={() => toggle('links')}>
                <LinksSection item={item} items={items} links={links} onOpenItem={onOpenItem} preset={linkPreset} onPresetUsed={() => setLinkPreset(null)} />
              </Fold>

              {target && (
                <Fold id="item-files" title="Attachments" count={attached.length} open={folds.has('files')} onToggle={() => toggle('files')}>
                  <AttachmentsPanel target={target} pasteAnywhere={!editing} openId={openFile} onOpenChange={setOpenFile} />
                </Fold>
              )}

              {missions.length > 0 && (
                <Fold id="item-missions" title="Missions" count={missions.length} open={folds.has('missions')} onToggle={() => toggle('missions')}>
                  <ul className="-mx-2 flex flex-col">
                    {missions.map((r) => (
                      <li key={r.mission.id}>
                        <Pressable className="flex flex-col gap-2 rounded-control px-2 py-2 hover:bg-hover" onClick={() => onOpenMission(r.mission.id)}>
                          <span className="flex items-center gap-3">
                            <MissionStatePill state={r.view.state} blockers={r.view.blockers} />
                            <span className="min-w-0 flex-1 truncate text-ui text-fg">{r.mission.title}</span>
                            <RelativeTime ts={r.mission.updatedAt} className="text-caption text-fg-subtle" />
                          </span>
                          <ProgressBar value={r.view.progress} label={`Progress of ${r.mission.title}`} caption={r.view.progressLabel} tone={r.view.state === 'failed' ? 'failed' : 'accent'} />
                        </Pressable>
                        <div className="px-2 pb-2">
                          <SessionApprovals sessionIds={r.mission.sessionIds} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </Fold>
              )}

              {item.source && (
                <Fold id="item-source" title="Where it came from" open={folds.has('source')} onToggle={() => toggle('source')}>
                  <SourceSection item={item} onOpenAttachment={setOpenFile} />
                </Fold>
              )}

            </div>
          </div>
        )}
      </Drawer>

      {item && live && <OverrideDialog item={item} status={override} open={override !== null} onOpenChange={(o) => !o && setOverride(null)} missionTitle={live.mission.title} />}

      <ConfirmDialog
        open={!!finishing && !!live}
        onOpenChange={(o) => !o && setFinishing(null)}
        title={`Stop mission "${live?.mission.title ?? ''}" and mark ${item?.key ?? 'the item'} ${finishing ? statusLabel(item?.kind ?? 'story', finishing).toLowerCase() : ''}?`}
        description="Its live sessions stop; work they did not commit stays in their folders."
        confirmLabel={finishing === 'dropped' ? 'Stop and drop' : 'Stop and mark done'}
        onConfirm={async () => {
          if (!item || !live || !finishing) return;
          const to = finishing;
          await stopMission(live.mission.id);
          await updateItem(item.id, { status: to });
          toast.success(to === 'done' ? 'Mission stopped, item done' : 'Mission stopped, item dropped', `${item.key} ${item.title}`);
          setFinishing(null);
        }}
      />

      <ConfirmDialog
        open={confirmStop && !!live}
        onOpenChange={setConfirmStop}
        title={`Stop mission "${live?.mission.title ?? ''}"?`}
        description="Its live sessions stop and the item goes back to ready."
        confirmLabel="Stop mission"
        onConfirm={async () => {
          if (!live) return;
          const r = await stopMission(live.mission.id);
          toast.success('Mission stopped', r.message);
          setConfirmStop(false);
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${item ? `${item.key} ${ITEM_KIND_LABEL[item.kind].toLowerCase()}` : 'item'} "${item?.title ?? ''}"?`}
        description="It and its links go for good; an item with children or missions can only be dropped."
        confirmLabel="Delete item"
        onConfirm={async () => {
          if (!item) return;
          const t = item.title;
          await deleteItem(item.id);
          onClose();
          toast.success('Item deleted', t);
        }}
      />
    </>
  );
}
